import axios from 'axios';

import { BASE_URL } from 'api/service_urls';
import request from 'utils/request';

export interface DocumentationHeading {
  id: string;
  text: string;
  level: number;
}

export interface DocumentationChapter {
  slug: string;
  title: string;
  order: number;
  markdown: string;
  // Present from documentation schema v2 onwards.
  path?: string;
  parent?: string | null;
  depth?: number;
  group?: string | null;
  summary?: string | null;
  last_reviewed?: string | null;
  routes?: string[];
  headings?: DocumentationHeading[];
  /** False hides the feedback widget on this page. */
  feedback?: boolean;
  /** False hides the "Follow along" button on this page. */
  follow_along?: boolean;
}

export interface ProductDocumentation {
  schema_version?: number;
  revision: string;
  title: string;
  chapters: DocumentationChapter[];
}

export const getProductDocumentation = (
  signal?: AbortSignal,
): Promise<ProductDocumentation> =>
  request(`${BASE_URL}/v1/profile/documentation/`, { signal });

export const getDocumentationAsset = (
  name: string,
  signal?: AbortSignal,
): Promise<Blob> =>
  request(
    `${BASE_URL}/v1/profile/documentation/assets/${encodeURIComponent(name)}`,
    { signal, responseType: 'blob' },
  );

export type DocumentationFeedbackRating = 'helpful' | 'neutral' | 'unhelpful';

export interface DocumentationFeedbackInput {
  page_path: string;
  /** Omitted or null for feedback about the whole page. */
  section_id?: string | null;
  revision: string;
  rating: DocumentationFeedbackRating;
  comment?: string | null;
}

export interface DocumentationFeedback extends DocumentationFeedbackInput {
  section_id: string | null;
  comment: string | null;
  last_updated_at: string | null;
}

export const saveDocumentationFeedback = (
  feedback: DocumentationFeedbackInput,
): Promise<DocumentationFeedback> =>
  request(`${BASE_URL}/v1/profile/documentation/feedback/`, {
    method: 'PUT',
    data: feedback,
  });

export const getDocumentationFeedback = (
  pagePath: string,
  signal?: AbortSignal,
): Promise<DocumentationFeedback[]> =>
  request(
    `${BASE_URL}/v1/profile/documentation/feedback/${pagePath
      .split('/')
      .map(encodeURIComponent)
      .join('/')}`,
    { signal },
  );

/**
 * A backend service's live OpenAPI document. Each service serves its own at
 * /v1/<service>/openapi.json, so the API reference matches what's deployed.
 */
export const getServiceOpenApi = (
  service: string,
  signal?: AbortSignal,
): Promise<any> =>
  request(`${BASE_URL}/v1/${encodeURIComponent(service)}/openapi.json`, {
    signal,
  });

export interface TryItResponse {
  status: number;
  statusText: string;
  durationMs: number;
  body: string;
}

/**
 * Sends an API reference "Try it" request as the signed-in user (the shared
 * axios interceptor adds the session token). Only same-origin paths built
 * from a documented operation reach here. Any status resolves, so the panel
 * can show errors as responses.
 */
export const sendTryItRequest = async (
  path: string,
  signal?: AbortSignal,
): Promise<TryItResponse> => {
  const started = performance.now();
  const response = await axios.get<string>(`${BASE_URL}${path}`, {
    signal,
    timeout: 30000,
    responseType: 'text',
    transformResponse: [data => data],
    validateStatus: () => true,
  });
  return {
    status: response.status,
    statusText: response.statusText,
    durationMs: Math.round(performance.now() - started),
    body: typeof response.data === 'string' ? response.data : '',
  };
};
